import { useCallback, useMemo, useState } from 'react';
import { X } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/ui/spinner';
import { Typography } from '@/components/ui/typography';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';

import {
  useServiceSelection,
  type PreviousServiceSelection,
} from '../../service-selection/hooks/useServiceSelection';
import { ServiceCardGrid } from '../../service-selection/components/ServiceCardGrid';
import { toServiceInfo } from '../utils/cartTransforms';
import { useFreeFloorPlanOffer } from '../floor-plan/useFreeFloorPlanOffer';
import { FloorPlanTierPanel } from '../floor-plan/FloorPlanTierPanel';
import { formatPriceDisplay } from '@/util/formatting';

import type { PropertyCartItem } from '../types/cart.types';
import type { DisplayService } from '../../../../types/order.types';

interface CartServiceSelectorProps {
  data: PropertyCartItem;
  onContinue: (data: PropertyCartItem) => void;
}

const CartServiceSelector = ({
  data,
  onContinue,
}: CartServiceSelectorProps) => {
  const previousSelections = useMemo<PreviousServiceSelection[] | null>(() => {
    if (!data.services) return null;
    return data.services.map((s) => ({
      shoppingCartSlug: s.shoppingCartSlug,
      quantity: s.quantity,
      price: s.price,
    }));
  }, [data.services]);

  const {
    services,
    isLoading,
    isLoadingAddOns,
    occupancyStatus,
    hasAnySelected,
    totals,
    selectService,
    deselectService,
    setServiceQuantity,
    changeOccupancy,
    clearSelection,
    buildSelectedServiceData,
  } = useServiceSelection({
    previousSelections,
    isCartOrder: true,
  });

  // DV-3204: the tier choice for the selected package. Picking Basic sends
  // nothing — order-service adds the free line when the order is created.
  const { tiersFor } = useFreeFloorPlanOffer({ entityUUID: data.entityUUID });
  const floorPlanTiers = useMemo(() => {
    const selectedSlugs = services
      .filter((s) => s.isSelected)
      .map((s) => s.shoppingCartSlug);
    return tiersFor(selectedSlugs);
  }, [services, tiersFor]);
  // A selected card follows the whole selection, like the panel: a floor plan
  // product picked alongside it cancels the free plan, so its badge goes
  // with the panel. An unselected card can only speak for itself.
  const offersFreeFloorPlan = useCallback(
    (service: DisplayService) =>
      service.isSelected
        ? floorPlanTiers !== null
        : tiersFor([service.shoppingCartSlug]) !== null,
    [floorPlanTiers, tiersFor],
  );

  // DV-3204: the slider has two service steps, as in the design — the
  // package (with its floor plan tier choice), then its add-ons.
  const [step, setStep] = useState<'packages' | 'add-ons'>('packages');

  const handleContinue = () => {
    const result = buildSelectedServiceData();
    if (!result) return;
    onContinue({
      ...data,
      services: result.services.map(toServiceInfo),
      price: result.totalPrice,
    });
  };

  const hasAddOns = services.some((s) => s.type === 'secondary');

  const handleContinueToAddOns = () => {
    // Alerts and returns null when no package is selected.
    if (!buildSelectedServiceData()) return;
    // A package with no add-ons skips their step. Continue waits for the
    // add-ons fetch (disabled below), so "none" here really means none.
    if (!hasAddOns) {
      handleContinue();
      return;
    }
    setStep('add-ons');
  };

  if (isLoading) {
    return (
      <div className="flex h-full items-center justify-center">
        <Spinner size="lg" />
      </div>
    );
  }

  const isAddOnsStep = step === 'add-ons';

  return (
    <section className="flex h-full flex-col">
      {/* Keyed by step so each step opens scrolled to the top. */}
      <div
        key={step}
        className="flex-1 min-h-0 space-y-5 overflow-y-auto p-4"
      >
        {isAddOnsStep ? (
          <>
            <Typography as="h3" variant="heading-md" className="text-lg leading-7">
              Select Additional Services
            </Typography>
            {isLoadingAddOns ? (
              <div className="flex justify-center py-10">
                <Spinner size="lg" />
              </div>
            ) : (
              <ServiceCardGrid
                services={services}
                view="add-ons"
                onSelect={selectService}
                onDeselect={deselectService}
                onQuantityChange={setServiceQuantity}
              />
            )}
          </>
        ) : (
          <>
            <div className="flex items-center justify-between">
              {totals.totalPrice <= 0 ? (
                <Typography as="h3" variant="heading-md" className="text-lg leading-7">
                  Select Your Primary Package
                </Typography>
              ) : (
                <>
                  <Typography as="h3" variant="heading-md" className="text-lg leading-7">
                    Selected Package
                  </Typography>
                  <Button variant="ghost" size="sm" onClick={clearSelection}>
                    <X className="h-4 w-4" />
                    Clear selection
                  </Button>
                </>
              )}
            </div>

            <div className="space-y-2">
              <Label className="text-sm font-medium">
                Property Occupancy Status
              </Label>
              <RadioGroup
                value={occupancyStatus}
                onValueChange={changeOccupancy}
                disabled={hasAnySelected}
                className="flex gap-4"
              >
                <div className="flex items-center gap-2">
                  <RadioGroupItem value="vacant" id="vacant" />
                  <Label htmlFor="vacant">Vacant</Label>
                </div>
                <div className="flex items-center gap-2">
                  <RadioGroupItem value="occupied" id="occupied" />
                  <Label htmlFor="occupied">Occupied</Label>
                </div>
              </RadioGroup>
            </div>

            <ServiceCardGrid
              services={services}
              view="packages"
              onSelect={selectService}
              onDeselect={deselectService}
              onQuantityChange={setServiceQuantity}
              offersFreeFloorPlan={offersFreeFloorPlan}
              belowPrimaries={
                floorPlanTiers && (
                  <FloorPlanTierPanel tiers={floorPlanTiers} />
                )
              }
            />
          </>
        )}
      </div>

      <div className="shrink-0 flex items-center border-t border-border bg-background px-5 py-3.5">
        {isAddOnsStep && (
          <Button variant="outline" onClick={() => setStep('packages')}>
            Back
          </Button>
        )}
        <div className="ml-auto flex items-center gap-5">
          <div className="flex items-baseline gap-1.5">
            <Typography variant="body-medium" className="text-muted-foreground">
              Total
            </Typography>
            <Typography variant="heading-md">
              ${formatPriceDisplay(totals.totalPrice)}
            </Typography>
          </div>
          <Button
            className="min-w-[100px]"
            // Waits while the package's add-ons load: the package step needs
            // them to know whether there is an add-ons step at all, and the
            // add-ons step must not be passed before they have been seen.
            disabled={isLoadingAddOns}
            onClick={isAddOnsStep ? handleContinue : handleContinueToAddOns}
          >
            Continue
          </Button>
        </div>
      </div>
    </section>
  );
};

export default CartServiceSelector;
